import React, { useState, useEffect } from 'react';
import { Package, Clock, CheckCircle, AlertCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';
import { format } from 'date-fns';
import StatusBadge, { backorderStatusTone } from '../shared/StatusBadge';

const EMPTY_SUMMARY = {
  totalPendingItems: 0,
  totalPartialItems: 0,
  totalFulfilledItems: 0,
  recentlyFulfilledItems: 0,
};

const receivedPercent = (fulfilled, total) => (total > 0 ? Math.round((fulfilled / total) * 100) : 0);

// Bump refreshKey to refetch.
const CustomerBackorderWidget = ({ userId, refreshKey = 0 }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [recentItems, setRecentItems] = useState([]);

  useEffect(() => {
    if (userId) {
      fetchBackorderData();
    }
  }, [userId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBackorderData = async () => {
    try {
      // Fetch summary
      const { data: summaryData, error: summaryError } = await supabase.rpc('get_customer_backorder_summary', {
        p_user_id: userId
      });

      if (summaryError) throw summaryError;
      setSummary(summaryData || EMPTY_SUMMARY);

      // Fetch recent items (show only active backorders - pending and partial)
      const { data: itemsData, error: itemsError } = await supabase.rpc('get_customer_backorder_items', {
        p_user_id: userId,
        p_status_filter: null,
        p_limit: 5
      });

      if (itemsError) throw itemsError;

      // Filter to show only pending and partially fulfilled items
      const activeItems = itemsData?.filter(item =>
        item.status === 'pending' || item.status === 'partially_fulfilled'
      ) || [];

      setRecentItems(activeItems);
    } catch (error) {
      console.error('Error fetching backorder data:', error);
      showError(error, 'Failed to load backorder information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (item) => {
    const { status, fulfilled_qty, backorder_qty } = item;
    const tone = backorderStatusTone(status);

    if (status === 'fulfilled') {
      return <StatusBadge tone={tone} icon={<CheckCircle className="w-3 h-3" aria-hidden="true" />}>Fulfilled</StatusBadge>;
    }

    if (status === 'partially_fulfilled') {
      return (
        <StatusBadge tone={tone} icon={<TrendingUp className="w-3 h-3" aria-hidden="true" />}>
          {receivedPercent(fulfilled_qty, backorder_qty)}% Received
        </StatusBadge>
      );
    }

    return <StatusBadge tone={tone} icon={<Clock className="w-3 h-3" aria-hidden="true" />}>Pending</StatusBadge>;
  };

  const getProgressBar = (fulfilled, total) => {
    const percentage = receivedPercent(fulfilled, total);

    return (
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>{fulfilled} of {total} received</span>
          <span>{percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${percentage === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return 'N/A';
    }
  };

  // Render nothing until we know there is something to show. A placeholder here
  // would appear and then vanish for the many customers with no backorders,
  // making the cards below it jump.
  const totalBackorders = summary.totalPendingItems + summary.totalPartialItems;
  if (loading || (totalBackorders === 0 && summary.recentlyFulfilledItems === 0)) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-500" aria-hidden="true" />
            My Backorders
          </h2>
          {totalBackorders > 0 && (
            <button
              onClick={() => history.push('/user/recent-orders')}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1 transition-colors"
            >
              View open orders
              <ArrowRight className="w-3 h-3" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Clock className="w-4 h-4 text-gray-500 mr-1" aria-hidden="true" />
              <p className="text-2xl font-bold text-gray-900 tabular-nums">{summary.totalPendingItems}</p>
            </div>
            <p className="text-xs text-gray-600">Pending</p>
          </div>

          <div className="text-center border-l border-r border-gray-300">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="w-4 h-4 text-gray-500 mr-1" aria-hidden="true" />
              <p className="text-2xl font-bold text-gray-900 tabular-nums">{summary.totalPartialItems}</p>
            </div>
            <p className="text-xs text-gray-600">Partly received</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <CheckCircle className="w-4 h-4 text-gray-500 mr-1" aria-hidden="true" />
              <p className="text-2xl font-bold text-gray-900 tabular-nums">{summary.recentlyFulfilledItems}</p>
            </div>
            <p className="text-xs text-gray-600">Received today</p>
          </div>
        </div>
      </div>

      {/* Recent Items */}
      <div className="px-6 py-4">
        {recentItems.length > 0 ? (
          <div className="space-y-3">
            {recentItems.map((item, index) => (
              <button
                key={`${item.order_id}-${item.part_number}-${index}`}
                onClick={() =>
                  history.push(`/user/recent-orders?search=${encodeURIComponent(item.order_number)}`)
                }
                className="w-full text-left border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                {/* Order Info */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900">Order #{item.order_number}</p>
                      <span className="text-xs text-gray-500">
                        {formatDate(item.order_created_at)}
                      </span>
                    </div>
                    <p className="text-sm font-mono text-gray-700">{item.part_number}</p>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-1">{item.description}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    {getStatusBadge(item)}
                  </div>
                </div>

                {/* Progress Bar for Partial Fulfillment */}
                {item.status === 'partially_fulfilled' && (
                  getProgressBar(item.fulfilled_qty, item.backorder_qty)
                )}

                {/* Pending Status Info */}
                {item.status === 'pending' && (
                  <div className="mt-2 flex items-center text-xs text-gray-500">
                    <AlertCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                    <span>Awaiting stock - {item.backorder_qty} units ordered</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm text-gray-600">No active backorders</p>
            {summary.recentlyFulfilledItems > 0 && (
              <p className="text-xs text-green-700 mt-1">
                {summary.recentlyFulfilledItems} item{summary.recentlyFulfilledItems > 1 ? 's' : ''} received today
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerBackorderWidget;
